<template>
  <div class="trend-chart-widget">
    <div class="chart-header">
      <div class="chart-title-section">
        <h3 class="chart-title">{{ title }}</h3>
        <p v-if="subtitle" class="chart-subtitle">{{ subtitle }}</p>
      </div>
      <div class="chart-controls">
        <div class="period-tabs">
          <button
            v-for="period in periods"
            :key="period.value"
            class="period-tab"
            :class="{ active: selectedPeriod === period.value }"
            @click="selectedPeriod = period.value"
          >
            {{ period.label }}
          </button>
        </div>
      </div>
    </div>

    <div class="chart-body">
      <div class="chart-top-row">
        <div class="chart-summary">
          <div class="summary-main">
            <span class="summary-value">{{ summaryValue }}</span>
            <span v-if="summaryTrend" class="summary-trend" :class="summaryTrend > 0 ? 'trend-up' : 'trend-down'">
              <i :class="summaryTrend > 0 ? 'fa fa-arrow-up' : 'fa fa-arrow-down'"></i>
              {{ Math.abs(summaryTrend) }}%
            </span>
          </div>
          <span class="summary-label">{{ summaryLabel }}</span>
        </div>

        <!-- Inline Stats (like Campaigns) -->
        <div v-if="inlineStats && inlineStats.length > 0" class="inline-stats">
          <div v-for="(stat, idx) in inlineStats" :key="idx" class="inline-stat">
            <span class="inline-stat-value">{{ stat.value }}</span>
            <span class="inline-stat-label">{{ stat.label }}</span>
          </div>
        </div>
      </div>

      <!-- SVG Area Chart -->
      <div class="chart-container">
        <svg class="area-chart" viewBox="0 0 400 120" preserveAspectRatio="none">
          <!-- Grid lines -->
          <g class="grid-lines">
            <line x1="0" y1="30" x2="400" y2="30" stroke="#f1f5f9" stroke-width="1" />
            <line x1="0" y1="60" x2="400" y2="60" stroke="#f1f5f9" stroke-width="1" />
            <line x1="0" y1="90" x2="400" y2="90" stroke="#f1f5f9" stroke-width="1" />
          </g>

          <!-- Gradient definition -->
          <defs>
            <linearGradient :id="'gradient-' + chartId" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" :stop-color="chartColor" stop-opacity="0.3" />
              <stop offset="100%" :stop-color="chartColor" stop-opacity="0.02" />
            </linearGradient>
          </defs>

          <!-- Area fill -->
          <path
            :d="areaPath"
            :fill="`url(#gradient-${chartId})`"
          />

          <!-- Line -->
          <path
            :d="linePath"
            fill="none"
            :stroke="chartColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />

          <!-- Data points -->
          <g class="data-points">
            <circle
              v-for="(point, index) in chartPoints"
              :key="index"
              :cx="point.x"
              :cy="point.y"
              r="4"
              :fill="chartColor"
              stroke="white"
              stroke-width="2"
              class="data-point"
            />
          </g>
        </svg>

        <!-- X-axis labels -->
        <div class="x-axis-labels">
          <span v-for="(label, index) in xAxisLabels" :key="index">{{ label }}</span>
        </div>
      </div>
    </div>

    <!-- Recent Items Section (optional, like Campaigns) -->
    <div v-if="recentItems && recentItems.length > 0" class="recent-section">
      <div class="recent-header">
        <span class="recent-title">{{ recentTitle }}</span>
        <router-link v-if="viewAllRoute" :to="viewAllRoute" class="view-all-link">
          View All <i class="fa fa-chevron-right"></i>
        </router-link>
      </div>
      <div class="recent-list">
        <div
          v-for="(item, idx) in recentItems"
          :key="idx"
          class="recent-item"
          :class="{ clickable: item.clickable }"
          @click="handleItemClick(item)"
        >
          <div class="item-avatar" :style="{ backgroundColor: item.avatarBg || chartColorLight }">
            <i :class="item.icon || defaultIcon" :style="{ color: item.iconColor || chartColor }"></i>
          </div>
          <div class="item-content">
            <div class="item-header">
              <span class="item-name">{{ item.name }}</span>
              <span v-if="item.badge" class="item-badge" :class="item.badgeClass">
                {{ item.badge }}
              </span>
            </div>
            <p class="item-meta">{{ item.meta }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty Recent State -->
    <div v-else-if="showRecentSection && !recentItems.length" class="recent-empty">
      <i :class="defaultIcon"></i>
      <span>{{ emptyMessage }}</span>
    </div>

    <div v-if="showLegend" class="chart-legend">
      <div class="legend-item" v-for="(item, index) in legendItems" :key="index">
        <span class="legend-dot" :style="{ backgroundColor: item.color }"></span>
        <span class="legend-label">{{ item.label }}</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TrendChart',

  props: {
    title: {
      type: String,
      default: 'Trend'
    },
    subtitle: {
      type: String,
      default: ''
    },
    data: {
      type: Array,
      default: () => [12, 19, 15, 25, 22, 30, 28]
    },
    labels: {
      type: Array,
      default: () => ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
    },
    summaryValue: {
      type: [String, Number],
      default: '0'
    },
    summaryLabel: {
      type: String,
      default: 'Total this week'
    },
    summaryTrend: {
      type: Number,
      default: null
    },
    chartColor: {
      type: String,
      default: '#3abf94'
    },
    showLegend: {
      type: Boolean,
      default: false
    },
    legendItems: {
      type: Array,
      default: () => []
    },
    // Inline stats shown next to summary (like Campaigns)
    inlineStats: {
      type: Array,
      default: () => []
      // Format: [{ value: '5', label: 'Active' }, ...]
    },
    // Recent items section (like Campaigns recent campaigns)
    recentItems: {
      type: Array,
      default: () => []
      // Format: [{ name, meta, icon, avatarBg, iconColor, badge, badgeClass, clickable, data }]
    },
    recentTitle: {
      type: String,
      default: 'Recent Activity'
    },
    viewAllRoute: {
      type: Object,
      default: null
    },
    showRecentSection: {
      type: Boolean,
      default: false
    },
    emptyMessage: {
      type: String,
      default: 'No recent activity'
    },
    defaultIcon: {
      type: String,
      default: 'fa fa-clock-o'
    }
  },

  data() {
    return {
      chartId: Math.random().toString(36).substr(2, 9),
      selectedPeriod: 'week',
      periods: [
        { label: 'Day', value: 'day' },
        { label: 'Week', value: 'week' },
        { label: 'Month', value: 'month' }
      ]
    };
  },

  computed: {
    xAxisLabels() {
      return this.labels;
    },

    chartPoints() {
      const data = this.data;
      if (!data || data.length === 0) return [];

      const width = 400;
      const height = 120;
      const padding = { top: 10, bottom: 20, left: 10, right: 10 };
      const chartWidth = width - padding.left - padding.right;
      const chartHeight = height - padding.top - padding.bottom;

      const max = Math.max(...data) || 1;
      const min = 0;

      return data.map((val, i) => ({
        x: padding.left + (i / (data.length - 1)) * chartWidth,
        y: padding.top + chartHeight - ((val - min) / (max - min)) * chartHeight
      }));
    },

    linePath() {
      const points = this.chartPoints;
      if (points.length === 0) return '';

      return points.reduce((path, point, i) => {
        if (i === 0) return `M ${point.x},${point.y}`;
        return `${path} L ${point.x},${point.y}`;
      }, '');
    },

    areaPath() {
      const points = this.chartPoints;
      if (points.length === 0) return '';

      const height = 120;
      const padding = { bottom: 20, left: 10, right: 10 };
      const width = 400;

      let path = `M ${padding.left},${height - padding.bottom}`;
      points.forEach((point, i) => {
        if (i === 0) {
          path += ` L ${point.x},${point.y}`;
        } else {
          path += ` L ${point.x},${point.y}`;
        }
      });
      path += ` L ${width - padding.right},${height - padding.bottom}`;
      path += ' Z';

      return path;
    },

    // Light version of chart color for avatar background
    chartColorLight() {
      // Convert hex to rgba with low opacity
      const hex = this.chartColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, 0.1)`;
    }
  },

  methods: {
    handleItemClick(item) {
      if (item.clickable) {
        this.$emit('item-click', item);
      }
    }
  },

  watch: {
    selectedPeriod(newPeriod) {
      this.$emit('period-change', newPeriod);
    }
  }
};
</script>

<style lang="scss" scoped>
.trend-chart-widget {
  background: white;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 24px 0;
}

.chart-title-section {
  flex: 1;
}

.chart-title {
  font-size: 1.0625rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
}

.chart-subtitle {
  font-size: 0.8125rem;
  color: #64748b;
  margin: 0;
}

.chart-controls {
  flex-shrink: 0;
}

.period-tabs {
  display: flex;
  background-color: #f1f5f9;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
}

.period-tab {
  padding: 6px 14px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #64748b;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: #334155;
  }

  &.active {
    background: white;
    color: #3abf94;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
}

.chart-body {
  padding: 16px 24px 16px;
}

.chart-top-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.chart-summary {
  display: inline-block;
}

// Inline Stats (like Campaigns)
.inline-stats {
  display: flex;
  gap: 24px;
}

.inline-stat {
  text-align: center;

  .inline-stat-value {
    display: block;
    font-size: 1.25rem;
    font-weight: 700;
    color: #334155;
    line-height: 1.2;
  }

  .inline-stat-label {
    font-size: 0.6875rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.summary-main {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 4px;
}

.summary-value {
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
}

.summary-trend {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 14px;

  i {
    font-size: 0.6875rem;
  }

  &.trend-up {
    background-color: rgba(34, 197, 94, 0.1);
    color: #16a34a;
  }

  &.trend-down {
    background-color: rgba(239, 68, 68, 0.1);
    color: #dc2626;
  }
}

.summary-label {
  font-size: 0.875rem;
  color: #64748b;
}

.chart-container {
  position: relative;
  width: 100%;
}

.area-chart {
  width: 100%;
  height: 140px;
  display: block;
}

.data-point {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.area-chart:hover .data-point {
  opacity: 1;
}

.x-axis-labels {
  display: flex;
  justify-content: space-between;
  padding: 8px 10px 0;

  span {
    font-size: 0.6875rem;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.chart-legend {
  display: flex;
  gap: 20px;
  padding: 14px 24px;
  border-top: 1px solid #f1f5f9;
  background-color: #fafbfc;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.legend-label {
  font-size: 0.8125rem;
  color: #64748b;
}

// Recent Section (like Campaigns)
.recent-section {
  border-top: 1px solid #e8ecf1;
}

.recent-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 24px 10px;
}

.recent-title {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.view-all-link {
  font-size: 0.8125rem;
  color: #3abf94;
  text-decoration: none;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.15s ease;

  i {
    font-size: 0.625rem;
  }

  &:hover {
    color: #2d9a78;
  }
}

.recent-list {
  padding: 0 0 8px;
}

.recent-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 24px;
  transition: background-color 0.15s ease;

  &.clickable {
    cursor: pointer;

    &:hover {
      background-color: #f8fafc;
    }
  }
}

.item-avatar {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 1rem;
  }
}

.item-content {
  flex: 1;
  min-width: 0;
}

.item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 2px;
}

.item-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.6875rem;
  font-weight: 500;
  flex-shrink: 0;

  &.badge-success {
    background-color: rgba(34, 197, 94, 0.1);
    color: #22c55e;
  }

  &.badge-info {
    background-color: rgba(59, 130, 246, 0.1);
    color: #3b82f6;
  }

  &.badge-warning {
    background-color: rgba(251, 191, 36, 0.1);
    color: #f59e0b;
  }

  &.badge-default {
    background-color: rgba(100, 116, 139, 0.1);
    color: #64748b;
  }
}

.item-meta {
  font-size: 0.75rem;
  color: #64748b;
  margin: 0;
}

// Empty State
.recent-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 24px;
  border-top: 1px solid #e8ecf1;
  color: #94a3b8;
  gap: 8px;

  i {
    font-size: 1.25rem;
    opacity: 0.6;
  }

  span {
    font-size: 0.8125rem;
  }
}
</style>
